import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { PlanTier, Feature, canAccess, AI_LIMIT, CREDITS_PER_PATIENT } from '../lib/planFeatures';
import { authedFetch } from './AuthContext';
import { useAuth } from './AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export interface PlanStatus {
  plan: PlanTier;
  effectivePlan: PlanTier;
  trialDaysLeft: number;
  trialStartDate?: string;
  subscriptionExpiresAt?: string;
  aiUsageToday: number;
  aiLimit: number;        // -1 = unlimited
  patientCount: number;
  patientLimit: number;   // -1 = unlimited
  credits: number;
  creditsGranted: boolean;
  canStartNewPatient: boolean;
}

export type UpgradeContext = Feature | 'general' | 'credits';

interface PlanContextType {
  status: PlanStatus | null;
  loading: boolean;
  /** Central gate — returns true if user can use this feature right now */
  can: (feature: Feature) => boolean;
  /** True if user has credits remaining OR is on a paid plan */
  hasCredits: boolean;
  /** True if user can start a new patient (credits ≥ 1000 or paid plan) */
  canStartPatient: boolean;
  /** Returns remaining AI calls today; -1 = unlimited */
  aiRemaining: number;
  /** Call before any AI operation. Returns true if allowed, false if limit hit (shows modal) */
  consumeAI: () => Promise<boolean>;
  /** Consume one step's worth of credits. Returns true if allowed. */
  consumeStepCredit: () => Promise<boolean>;
  /** Trigger upgrade modal with context */
  openUpgrade: (context?: UpgradeContext) => void;
  closeUpgrade: () => void;
  upgradeOpen: boolean;
  upgradeContext: UpgradeContext;
  /** Refresh plan status from server */
  refresh: () => Promise<void>;
  /** Simulate upgrade — in production, integrate Razorpay here */
  upgrade: (plan: 'basic' | 'premium' | 'plus' | 'pro') => Promise<void>;
}

const PlanContext = createContext<PlanContextType | undefined>(undefined);

export const PlanProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [status, setStatus] = useState<PlanStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState<UpgradeContext>('general');
  const fetchedRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || user?.role === 'admin') return;
    setLoading(true);
    try {
      const res = await authedFetch(`${API_BASE}/plan/status`);
      const data = await res.json();
      if (data.success) setStatus(data.data);
    } catch {
      // silently fail — app still works, just no plan enforcement
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.role]);

  // Fetch once on auth
  useEffect(() => {
    if (isAuthenticated && !fetchedRef.current) {
      fetchedRef.current = true;
      refresh();
    }
    if (!isAuthenticated) {
      fetchedRef.current = false;
      setStatus(null);
    }
  }, [isAuthenticated, refresh]);

  const effectivePlan: PlanTier = status?.effectivePlan ?? 'free';
  const isPaidPlan = !!user?.demoAccess || effectivePlan === 'basic' || effectivePlan === 'premium';

  const hasCredits = isPaidPlan || (status?.credits ?? 0) > 0;
  const canStartPatient = isPaidPlan || (status?.canStartNewPatient ?? true);

  const can = useCallback(
    (feature: Feature): boolean => {
      if (user?.demoAccess) return true;
      if (!status) return true; // optimistic until status loaded
      // Paid plans: use their plan tier access rules
      if (isPaidPlan) return canAccess(feature, effectivePlan);
      // Export/branding always require a paid plan — credits don't unlock them
      if (feature === 'export' || feature === 'branding') return false;
      // Credits remaining → full feature access (starter/trial period)
      if ((status.credits ?? 0) > 0) return true;
      // Credits exhausted on a free/trial plan → fall back to free-tier access only
      // (keeps basic assessment steps open, locks export/voice/analytics/sharing)
      return canAccess(feature, 'free');
    },
    [user?.demoAccess, status, effectivePlan, isPaidPlan]
  );

  const aiRemaining = (() => {
    if (!status) return -1;
    const limit = AI_LIMIT[effectivePlan];
    if (limit === -1) return -1;
    return Math.max(0, limit - status.aiUsageToday);
  })();

  const consumeAI = useCallback(async (): Promise<boolean> => {
    if (user?.demoAccess) return true;
    if (!status) return true; // no status = optimistic allow
    const remaining = AI_LIMIT[effectivePlan];
    if (remaining === -1) return true; // unlimited

    try {
      const res = await authedFetch(`${API_BASE}/plan/ai-use`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        // Update local count
        setStatus((prev) => prev ? { ...prev, aiUsageToday: data.data.used } : prev);
        if (!data.data.allowed) {
          openUpgrade('ai');
          return false;
        }
        return true;
      }
      // API returned success:false — show a toast so user knows why button didn't work
      toast.error('Could not verify AI access. Please try again.');
      return false;
    } catch {
      toast.error('Network error checking AI access. Please try again.');
      return false; // fail closed so the user sees the error
    }
  }, [user?.demoAccess, status, effectivePlan]);

  const consumeStepCredit = useCallback(async (): Promise<boolean> => {
    if (user?.demoAccess) return true;
    if (!status) return true;
    if (isPaidPlan) return true; // paid users don't consume credits
    if ((status.credits ?? 0) <= 0) {
      openUpgrade('credits');
      return false;
    }
    try {
      const res = await authedFetch(`${API_BASE}/plan/consume-credit`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setStatus((prev) => prev
          ? {
            ...prev,
            credits: data.data.creditsRemaining,
            canStartNewPatient: data.data.creditsRemaining >= CREDITS_PER_PATIENT,
          }
          : prev
        );
        return data.data.allowed;
      }
      return true; // fail open
    } catch {
      return true;
    }
  }, [user?.demoAccess, status, isPaidPlan]);

  const openUpgrade = useCallback((context: UpgradeContext = 'general') => {
    setUpgradeContext(context);
    setUpgradeOpen(true);
  }, []);

  const closeUpgrade = useCallback(() => setUpgradeOpen(false), []);

  const upgrade = useCallback(async (plan: 'basic' | 'premium' | 'plus' | 'pro') => {
    try {
      const res = await authedFetch(`${API_BASE}/plan/upgrade`, {
        method: 'POST',
        body: JSON.stringify({ plan }),
      });
      const data = await res.json();
      if (data.success) {
        await refresh();
        closeUpgrade();
      }
    } catch {
      // ignore
    }
  }, [refresh, closeUpgrade]);

  // patientLimitValue exposed via status.patientLimit from API

  return (
    <PlanContext.Provider value={{
      status,
      loading,
      can,
      hasCredits,
      canStartPatient,
      aiRemaining,
      consumeAI,
      consumeStepCredit,
      openUpgrade,
      closeUpgrade,
      upgradeOpen,
      upgradeContext,
      refresh,
      upgrade,
    }}>
      {children}
    </PlanContext.Provider>
  );
};

export const usePlan = (): PlanContextType => {
  const ctx = useContext(PlanContext);
  if (!ctx) throw new Error('usePlan must be used inside PlanProvider');
  return ctx;
};

